import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiSearch } from '../api/client.ts';
import { queryDefaults, useMe } from '../api/queries.ts';
import { DashboardTasks } from '../components/tasks/DashboardTasks.tsx';
import { useSession } from '../auth/session.tsx';
import { useToolbar } from '../api/navigation.ts';
import { formatDate } from '../dates.ts';
import { Panel } from '../components/Panel.tsx';
import { useRealEstate } from '../api/properties.ts';

/**
 * `/home` — the reader's day (docs/navigation-brief.md, section 3).
 *
 * - Tiers 1-2: a snapshot card per department, each the toolbar's own count
 *   with a link to its module. No new figure: the same numbers, larger.
 * - A Social Work case manager: their visits due and their tasks.
 * - Anyone else's Home is their landing, so /home sends them there.
 */
export function Home() {
  const me = useMe();
  const toolbar = useToolbar();
  if (!me.data || !toolbar.data) return <p className="p-6 text-neutral-600">Loading…</p>;
  if (me.data.role.code === 'CASE_MGR_SW') return <YourDay />;
  if (toolbar.data.landing !== '/home') return <Navigate to={toolbar.data.landing} replace />;
  return <Snapshot />;
}

/**
 * The departments on the tiers 1-2 Home, in the approved mockup's order: one
 * card each, three across at 4 units, two rows. Clients, and the reserved
 * Finance and Documents, are on the toolbar and not cards.
 */
const DEPARTMENTS: { key: string; sub: string }[] = [
  { key: 'visits', sub: 'clients overdue or due within 30 days' },
  { key: 'legal', sub: 'commissions outstanding' },
  { key: 'reports', sub: 'reports overdue or due within 30 days' },
  { key: 'compensation', sub: 'collectable this month' },
  { key: 'tasks', sub: 'your open tasks' },
  { key: 'real_estate', sub: 'active properties' },
];

function Snapshot() {
  const toolbar = useToolbar();
  const modules = toolbar.data!.modules;
  const hasRealEstate = modules.some((m) => m.key === 'real_estate');
  // Real Estate has no toolbar count; its card counts the active list, the same answer the page shows.
  const realEstate = useRealEstate('active', hasRealEstate);
  const cards = DEPARTMENTS.flatMap((d) => {
    const m = modules.find((x) => x.key === d.key);
    if (!m) return [];
    const count = d.key === 'real_estate' ? realEstate.data?.properties.length ?? null : m.count;
    return [{ ...d, label: m.label, path: m.path, count }];
  });
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] px-4 py-5 lg:px-6">
        <h1 className="sr-only">The agency today</h1>
        <p className="mb-3 text-sm text-neutral-700">
          Each department at a glance, counted across every client you can see. The full figures are in Agency
          overview, under Agency at the top right.
        </p>
        <div className="grid12">
          {cards.map((c) => (
            <Panel key={c.key} title={c.label} span={4}>
              <div className="grid gap-1 px-4 py-3">
                <span className="font-mono text-[1.75rem] leading-none font-medium text-neutral-900 tabular-nums">
                  {c.count === null ? '…' : c.count.toLocaleString()}
                </span>
                <span className="text-sm text-neutral-700">{c.sub}</span>
                <Link to={c.path} className="link inline-flex min-h-[44px] items-center text-sm font-semibold">Open {c.label}</Link>
              </div>
            </Panel>
          ))}
        </div>
      </div>
    </div>
  );
}

interface DueRow {
  client_id: string;
  client_ref: string;
  display_name: string;
  currently_at: string | null;
  lives_at: string | null;
  due_on: string | null;
  days_until: number | null;
  is_overdue: boolean;
}

function YourDay() {
  const { getToken } = useSession();
  const due = useQuery({
    queryKey: ['visits', 'due', 'home'],
    queryFn: () => apiSearch<{ total: number; visits: DueRow[] }>('/visits/due', { soon: 'true', limit: 12, sort: '-priority' }, getToken),
    ...queryDefaults,
  });
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto grid max-w-[96rem] gap-4 px-4 py-6 lg:grid-cols-2 lg:px-6">
        <section className="panel">
          <header className="panel-bar">
            <h1>Your visits due</h1>
            <Link to="/visits" className="inline-flex min-h-[44px] items-center text-on-brand underline normal-case tracking-normal">All visits due</Link>
          </header>
          {!due.data ? (
            <p className="px-4 pb-4 text-neutral-600">{due.error ? 'Your visits could not be loaded.' : 'Loading…'}</p>
          ) : due.data.visits.length === 0 ? (
            <p className="px-4 pb-4 text-neutral-600">Nobody is due a visit in the next 30 days.</p>
          ) : (
            <ul className="divide-y divide-neutral-100 pb-2">
              {due.data.visits.map((v) => (
                <li key={v.client_id}>
                  <Link to={`/clients/${v.client_id}`} className="flex min-h-[52px] flex-wrap items-center gap-x-3 px-4 py-2 hover:bg-neutral-50">
                    <span className="min-w-0 flex-1">
                      <span className="font-semibold text-neutral-900">{v.display_name}</span>{' '}
                      <span className="font-mono text-sm text-neutral-500">{v.client_ref}</span>
                      <span className="block truncate text-sm text-neutral-600">{v.currently_at ?? v.lives_at ?? 'Not recorded'}</span>
                    </span>
                    <span className={`text-sm ${v.is_overdue ? 'font-semibold text-critical-edge' : 'text-neutral-700'}`}>
                      {v.due_on ? formatDate(v.due_on) : 'No date'}
                      {v.is_overdue && v.days_until !== null && ` · ${-v.days_until} days overdue`}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {due.data && due.data.total > due.data.visits.length && (
            <p className="px-4 pb-3 text-sm text-neutral-500">{due.data.total - due.data.visits.length} more in Visits due.</p>
          )}
        </section>

        <DashboardTasks />
      </div>
    </div>
  );
}
